// TODO(sierra-widget-removal): initSierraChat / preloadSierraScript / sierra.less are unused after Shippy/Sierra split — delete in follow-up PR. SIERRA_AGENT_TOKEN is still consumed by the Shippy sidebar's Sierra handoff; move it to a dedicated module when the widget code is removed.
// please note that there are changes to ss-ui-client/src/ui-components/components/global-event-listener/context.js that were made that are required for this to work.
import { DateTime } from 'luxon';
import { defineMessages } from 'react-intl';

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import getIntl from '../../../intl/get-intl';
import { AnalyticsService } from '../../../services/analytics-service';
import { LogService } from '../../../services/log-service';
import { getPartitionNumber } from '../../../services/partition-service';
import './sierra.less';

const ssProductVersion = 'v3';

export const SIERRA_AGENT_TOKEN = 'PF411fkBl1wuKbIBmFxg95VxgJrp-2I1z5EU-QLGZYA';

const messages = defineMessages({
  errorTitle: {
    defaultMessage: 'Unable to start chat',
    id: 'Be/kfN',
    description: 'Error notification title for Sierra chat failure',
  },
  errorMessage: {
    defaultMessage: 'We were unable to start the chat service. Please try refreshing the page.',
    id: 'Chy3IT',
    description: 'Error notification message for Sierra chat failure',
  },
  confirmQuitMessage: {
    defaultMessage: 'Are you sure you want to end this chat?',
    id: 'RSNI1e',
    description: 'Message asking if the user wants to leave sierra chat',
  },
  disclosure: {
    defaultMessage:
      'By starting a chat with our artificial intelligence-powered assistant, you agree to the automated processing of your personal data.',
    id: 'pzv6v9',
    description: 'Disclosure for interacting with sierra chat',
  },
  inputPlaceholder: {
    defaultMessage: 'Type a message…',
    id: 'Qb52/q',
    description: 'Placeholder for input for sierra chat',
  },
  chatErrorMessage: {
    defaultMessage: 'Oops, an error was encountered! Please try again.',
    id: '/91c2p',
    description: 'Error message shown if sierra encounters an error',
  },
  agentTransferWaitingMessage: {
    defaultMessage: 'Waiting for agent…',
    id: 'aSh7+H',
    description: 'Message shown when waiting for sierra agent',
  },
  agentTransferQueueSizeMessage: {
    defaultMessage: 'Queue Size: {QUEUE_SIZE}',
    id: '5PnQM8',
    description: 'Message showing queue size when waiting for agent transfer',
  },
  agentTransferQueueNextMessage: {
    defaultMessage: 'You are next in line',
    id: 'X4SBd+',
    description: 'Message shown when user is next in queue for agent transfer',
  },
  agentJoinedMessage: {
    defaultMessage: 'Agent connected',
    id: 'GeQR+n',
    description: 'Message shown when agent joins the chat',
  },
  agentLeftMessage: {
    defaultMessage: 'Agent disconnected',
    id: 'TM42r8',
    description: 'Message shown when agent leaves the chat',
  },
  conversationEndedMessage: {
    defaultMessage: 'Chat ended',
    id: 'Wf96E/',
    description: 'Message shown when conversation has ended',
  },
  endConversationMenuLabel: {
    defaultMessage: 'End chat',
    id: 's+3M49',
    description: 'Label for menu option to end the conversation',
  },
  printTranscriptMenuLabel: {
    defaultMessage: 'Print transcript',
    id: 'YYZj/l',
    description: 'Label for menu option to print the chat transcript',
  },
  newTabScreenReaderText: {
    defaultMessage: '(opens in a new tab)',
    id: '5QIVsF',
    description: 'Screen reader text indicating link opens in new tab',
  },
  confirmEndConversationDialogText: {
    defaultMessage: 'Are you sure you want to end this chat?',
    id: 'tpl/jP',
    description: 'Confirmation dialog text when ending conversation',
  },
  confirmEndConversationDialogCancelButtonLabel: {
    defaultMessage: 'Cancel',
    id: 'xHXJbe',
    description: 'Label for cancel button in end conversation dialog',
  },
});

const localeCodeMap = {
  en: 'en-US',
  es: 'es-US',
  fr: 'fr-FR',
  de: 'de-DE',
};

const getMappedLocale = (locale) => {
  return localeCodeMap[locale] || locale;
};

let initialized = false;

/**
 * Format plan level for Sierra
 * @param {object} subscriptionPlan - Subscription plan object
 * @returns {string} Formatted plan level
 */
const formatPlanLevel = (subscriptionPlan) => {
  if (!subscriptionPlan?.planName) return 'unknown';

  // Format plan name to lowercase, remove spaces per Sierra requirements
  return subscriptionPlan.planName.replace(/\s/g, '').toLowerCase();
};

/**
 * Format date for Sierra (YYYY/MM/DD format)
 * @param {string} dateString - ISO date string
 * @returns {string|null} Formatted date
 */
const formatDate = (dateString) => {
  if (!dateString) return null;

  try {
    return DateTime.fromISO(dateString).toFormat('yyyy/MM/dd');
  } catch {
    LogService.logWarning('Invalid date format for Sierra:', dateString);
    return null;
  }
};

const showErrorNotification = (intl) => {
  NotificationService.showNotification({
    notificationType: NOTIFICATION_TYPE_ERROR,
    title: intl?.formatMessage(messages.errorTitle) || messages.errorTitle.defaultMessage,
    content: intl?.formatMessage(messages.errorMessage) || messages.errorMessage.defaultMessage,
  });
};

const loadSierraScriptTag = () => {
  window.sierraConfig = { waitForInit: true };
  const sierraUrl = `https://sierra.chat/agent/${SIERRA_AGENT_TOKEN}/embed`;

  // Load Sierra SDK as module script
  const script = document.createElement('script');
  script.type = 'module';
  script.src = sierraUrl;
  script.onerror = () => {
    throw new Error(`Failed to load Sierra SDK from ${sierraUrl}`);
  };
  document.head.appendChild(script);

  // Wait for Sierra to be available
  return new Promise((resolve, reject) => {
    let attempts = 0;
    const maxAttempts = 100; // 10 seconds max

    const checkSierra = () => {
      if (window.sierra) {
        resolve();
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(checkSierra, 100);
      } else {
        reject(new Error('Sierra SDK failed to load within timeout'));
      }
    };

    checkSierra();
  });
};

/**
 * Preload Sierra script at app startup without initializing
 * This allows the script to be ready when the user clicks "Chat with us"
 */
export const preloadSierraScript = async () => {
  if (window.sierra) {
    LogService.logInfo('Sierra script already loaded');
    return;
  }

  try {
    await loadSierraScriptTag();
    LogService.logInfo('Sierra script preloaded successfully');
  } catch (error) {
    LogService.logWarning('Failed to preload Sierra script:', error);
    // Don't throw - this is a non-critical preload operation
  }
};

// this was provided by the sierra devs
const svgLogo = `
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <path d="M23.999 10.1604C22.2698 10.1604 20.8678 8.75842 20.8678 7.0292C20.8678 6.16507 21.2182 5.38238 21.7845 4.81562C21.7845 4.81562 21.785 4.81513 21.7854 4.81465L19.1844 2.21358C19.1844 2.21358 19.1839 2.21406 19.1834 2.21455C18.6171 2.78083 17.8344 3.13121 16.9703 3.13121C15.2411 3.13121 13.8391 1.72923 13.8391 0H10.1609C10.1609 1.72971 8.7589 3.13169 7.02968 3.13169C6.16506 3.13169 5.38287 2.78132 4.81658 2.21503C4.81658 2.21503 4.8161 2.21455 4.81561 2.21406L2.21455 4.81513C2.21455 4.81513 2.21455 4.81561 2.21551 4.8161C2.7818 5.38287 3.13217 6.16507 3.13217 7.02968C3.13217 8.75891 1.73019 10.1609 0.000963893 10.1609L0 13.8396C1.72923 13.8396 3.13217 15.2416 3.13217 16.9708C3.13217 17.8349 2.7818 18.6176 2.21551 19.1844C2.21551 19.1844 2.21503 19.1849 2.21455 19.1853L4.81561 21.7864C4.81561 21.7864 4.8161 21.7859 4.81658 21.7855C5.38287 21.2192 6.16555 20.8693 7.02968 20.8693C8.7589 20.8693 10.1609 22.2713 10.1609 24.0005H13.8391C13.8391 22.2708 15.2411 20.8688 16.9703 20.8688C17.8349 20.8688 18.6171 21.2192 19.1834 21.785C19.1834 21.785 19.1839 21.7855 19.1844 21.7859L21.7854 19.1849C21.7854 19.1849 21.7854 19.1844 21.7845 19.1839C21.2182 18.6171 20.8678 17.8349 20.8678 16.9703C20.8678 15.2411 22.2698 13.8391 23.999 13.8391V10.1604ZM18.0263 15.4792L11.9995 18.9588L5.97277 15.4792V8.51986L11.9995 5.0402L18.0263 8.51986V15.4792Z" fill="#22EE88"/>
</svg>
`;

/**
 * Build Sierra configuration object
 * @param {object} userConfig - User configuration
 * @param {string} [chatInitOrigin] - Optional origin identifier for the chat (e.g., 'app', 'error_modal')
 * @returns {unknown} Sierra configuration
 */
const buildSierraConfig = (userConfig, chatInitOrigin, intl) => {
  return {
    // these were provided by the sierra dev team.
    display: 'corner',
    dimensions: {
      width: '365px', // 25rem
      height: '40rem',
    },
    logoSVG: svgLogo,
    titleBarLogoOnly: false,
    canRenderMessageImages: false,
    colors: {
      background: '#ffffff',
      text: '#0F0F10',
      border: '#FFFFFF',
      titleBar: '#00331C',
      titleBarText: '#ffffff',
      assistantBubble: '#F1F2F3',
      assistantBubbleText: '#0F0F10',
      userBubble: '#F1F2F3',
      userBubbleText: '#0F0F10',
    },
    chatInterfaceStrings: {
      name: 'ShipStation',
      disclosure: intl.formatMessage(messages.disclosure),
      inputPlaceholder: intl.formatMessage(messages.inputPlaceholder),
      errorMessage: intl.formatMessage(messages.chatErrorMessage),
      agentTransferWaitingMessage: intl.formatMessage(messages.agentTransferWaitingMessage),
      agentTransferQueueSizeMessage: intl.formatMessage(messages.agentTransferQueueSizeMessage),
      agentTransferQueueNextMessage: intl.formatMessage(messages.agentTransferQueueNextMessage),
      agentJoinedMessage: intl.formatMessage(messages.agentJoinedMessage),
      agentLeftMessage: intl.formatMessage(messages.agentLeftMessage),
      conversationEndedMessage: intl.formatMessage(messages.conversationEndedMessage),
      endConversationMenuLabel: intl.formatMessage(messages.endConversationMenuLabel),
      printTranscriptMenuLabel: intl.formatMessage(messages.printTranscriptMenuLabel),
      newTabScreenReaderText: intl.formatMessage(messages.newTabScreenReaderText),
      confirmEndConversationDialogText: intl.formatMessage(messages.confirmEndConversationDialogText),
      confirmEndConversationDialogCancelButtonLabel: intl.formatMessage(messages.confirmEndConversationDialogCancelButtonLabel),
    },
    waitForConversationEnd: true,
    canEndConversation: true,
    confirmEndConversation: true,
    titleBarEndConversationButton: true,
    onConversationEnd: () => {
      window.sierra.closeChatModal();
      window.sierra.cleanup();
      initialized = false;
    },
    canStartNewChat: true,
    canPrintTranscript: false,
    customHideTitleBar: true,
    unstyled: true,
    launcher: {
      backgroundColor: '#00331C',
      textColor: '#ffffff',
      borderColor: '#cccccc',

      hoverBackgroundColor: '#ffffff',
      hoverTextColor: '#00331C',
      hoverBorderColor: '#cccccc',

      text: '',
      icon: 'oval', // "oval", "rectangle" or "none"

      proactiveBanner: {
        text: "👋 Hi, I'm AuctoBot, your virtual assistant! How can I help?",
        showAfterSeconds: 10,
        hideAfterSeconds: 30,
      },
      notificationColor: 'ffffff',

      showUnreadIndicator: true,
      unreadIndicatorColor: '#22EE88',
      unreadIndicatorText: 'New message',
      unreadIndicatorIcon: 'circle',

      customStyle: {
        bottom: '150px',
        right: '10px',
        zIndex: '100',
      },
    },

    // this is our code for getting the widget to work in the app
    userIdentityToken: userConfig.userId?.toString(),
    persistence: 'cookie',
    cookieDomain: 'shipstation.com',
    locale: getMappedLocale(userConfig.locale),
    onLoad: () => {
      // Set data-ignore-root-focus as soon as widget loads to prevent global focus manager from interfering
      const element = document.querySelector('[data-sierra-chat-container]');
      if (element) {
        element.setAttribute('data-ignore-root-focus', '');
      }
    },
    onError: (error) => LogService.logError(new Error('Sierra Chat widget error', { cause: error })),
    onUserMessage: () =>
      AnalyticsService.trackEvent('Sierra Chat Message', {
        messageType: 'unknown',
        timestamp: DateTime.now().toISO(),
      }),
    onOpen: () => {
      // we must do this so that the global event listener doesn't eat all the events in the chat widget
      const element = document.querySelector('[data-sierra-chat-container]');
      element.setAttribute('data-ignore-root-focus', '');

      // Focus the input field
      const input = element.shadowRoot.querySelector('textarea[part="chat-input"]');
      if (input) {
        input.focus();
      }
    },
    hideOnClose: true,
    resize: {},
    variables: {
      requesterName: userConfig.name || '',
      requesterEmail: userConfig.email || '',
      brand: 'shipstation', // Always lowercase 'shipstation'
      sellerId: userConfig.sellerId?.toString() || '',
      planLevel: formatPlanLevel(userConfig.subscriptionPlan),
      accountCountry: userConfig.homeCountry?.toLowerCase() || 'us',
      ssProductVersion,
      userAdmin: userConfig.userHasAdmin?.toString() || 'unknown',
      messagingOrigin: chatInitOrigin || 'app',
      organizationIdSeller: userConfig.organizationIdSeller || '',
      appLanguage: getMappedLocale(userConfig.locale),
      ssPlanVolume: userConfig.ssPlanVolume || 'unknown',
      ssEnvironment: userConfig.ssEnvironment || `ss${getPartitionNumber()}`,
      billingCycleDate: formatDate(userConfig.billingCycleDate),
      startDate: formatDate(userConfig.activationDate),
      features: userConfig.features || '[]',
    },
  };
};

const setFileUploadIconStyle = () => {
  // this is from the sierra dev.
  setTimeout(() => {
    // Find the main chat widget container
    const chatContainer = document.querySelector('div[data-sierra-chat-container]');

    // Check if the widget and its Shadow DOM exist
    if (chatContainer && chatContainer.shadowRoot) {
      // Create a new <style> element
      const style = document.createElement('style');

      // Define a CSS rule that only targets the "Upload file" SVG
      style.textContent = `
            svg[data-name="IconPhoto"] {
            fill: #3e3e42 !important;
            padding: 0px !important;
            height: 20px !important;
            width: 20px !important;
            }
            `;

      // Append the new style rule directly into the widget's Shadow DOM
      chatContainer.shadowRoot.appendChild(style);
    }
  }, 200); // 200ms delay
};

export const initSierraChat = async (userConfig, chatInitOrigin) => {
  if (initialized) {
    window.sierra.openChatModal();
    setFileUploadIconStyle();
    return;
  }
  try {
    // Script may already be preloaded, but load it if needed
    if (!window.sierra) {
      await loadSierraScriptTag();
    }

    const intl = await getIntl(userConfig.locale);
    const sierraConfig = buildSierraConfig(userConfig, chatInitOrigin, intl);
    window.sierra.init(sierraConfig);
    window.sierra.clearPersistedConversation();
    window.sierra.openChatModal();
    setFileUploadIconStyle();
    initialized = true;
    LogService.logInfo('Sierra Chat initialized successfully', {});
  } catch (error) {
    LogService.logError(new Error('Sierra Chat initialization failed', { cause: error }));
    const intl = await getIntl(userConfig.locale);
    showErrorNotification(intl);
    throw error;
  }
};
